import { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminLayout } from '../../layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  Plus, 
  Users,
  Trash2,
  Edit,
  Loader2,
  Wallet,
  Shield,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

function safeNumber(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [protectedUsername, setProtectedUsername] = useState('');
  const [editPassword, setEditPassword] = useState('');

  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    role: 'trader',
    balance: 1000,
    liability_limit: 5000,
    show_exposure: true,
    disable_limits: false
  });

  const [editForm, setEditForm] = useState({
    balance: 0,
    liability_limit: 0,
    show_exposure: true,
    disable_limits: false
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchProtectedUsername();
  }, []);

  const fetchProtectedUsername = async () => {
    try {
      const response = await axios.get(`${API}/system/settings`);
      setProtectedUsername(response.data.protected_username || '');
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.username || !userForm.password) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/users`, {
        ...userForm,
        balance: safeNumber(userForm.balance, 1000),
        liability_limit: safeNumber(userForm.liability_limit, 5000),
      });
      toast.success('User created successfully');
      setShowCreateUser(false);
      setUserForm({
        username: '',
        password: '',
        role: 'trader',
        balance: 1000,
        liability_limit: 5000,
        show_exposure: true,
        disable_limits: false
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      balance: user.balance,
      liability_limit: user.liability_limit,
      show_exposure: user.show_exposure !== false,
      disable_limits: user.disable_limits === true
    });
    setEditPassword('');
    setShowEditUser(true);
  };

  const handleUpdateUser = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, editForm);

      if (editPassword) {
        await axios.post(`${API}/users/${selectedUser.id}/password`, {
          new_password: editPassword,
          username: selectedUser.username,
        });
        toast.success(`User updated and password reset for ${selectedUser.username}`);
      } else {
        toast.success('User updated successfully');
      }

      setShowEditUser(false);
      setSelectedUser(null);
      setEditPassword('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="user-management">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-zinc-100">User Management</h1>
            <p className="text-sm text-zinc-500 mt-1">Manage trader accounts and limits</p>
          </div>
          <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                New User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-zinc-100">Create New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Username *</Label>
                  <Input
                    placeholder="Enter username"
                    value={userForm.username}
                    onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                    className="bg-black border-zinc-800 text-zinc-100"
                    data-testid="username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter password"
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      className="bg-black border-zinc-800 text-zinc-100 pr-10"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      data-testid="toggle-password-btn"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Role</Label>
                  <Select
                    value={userForm.role}
                    onValueChange={(value) => setUserForm({ ...userForm, role: value })}
                  >
                    <SelectTrigger className="bg-black border-zinc-800 text-zinc-100" data-testid="role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="trader">Trader</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Initial Balance</Label>
                    <Input
                      type="number"
                      value={userForm.balance}
                      onChange={(e) =>
                        setUserForm({
                          ...userForm,
                          balance: safeNumber(e.target.value, userForm.balance),
                        })
                      }
                      className="bg-black border-zinc-800 text-zinc-100"
                      data-testid="balance-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Liability Limit</Label>
                    <Input
                      type="number"
                      value={userForm.liability_limit}
                      onChange={(e) =>
                        setUserForm({
                          ...userForm,
                          liability_limit: safeNumber(e.target.value, userForm.liability_limit),
                        })
                      }
                      className="bg-black border-zinc-800 text-zinc-100"
                      data-testid="liability-input"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" className="border-zinc-700 text-zinc-400">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={handleCreateUser}
                  disabled={submitting}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-user-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create User'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Users Table */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-zinc-100 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Users ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-12 bg-zinc-800 animate-pulse rounded" />
                ))}
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No users created yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400">Username</TableHead>
                      <TableHead className="text-zinc-400">Role</TableHead>
                      <TableHead className="text-zinc-400 text-right">Balance</TableHead>
                      <TableHead className="text-zinc-400 text-right">Liability Limit</TableHead>
                      <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(user => (
                      <TableRow 
                        key={user.id} 
                        className="border-zinc-800"
                        data-testid={`user-row-${user.id}`}
                      >
                        <TableCell className="font-medium text-zinc-100">
                          <div className="flex items-center gap-2">
                            {user.username}
                            {user.disable_limits && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase bg-red-500/20 text-red-400 border border-red-500/30 rounded">
                                No Limits
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                            user.role === 'admin' 
                              ? 'bg-purple-500/20 text-purple-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {user.role === 'admin' && <Shield className="w-3 h-3" />}
                            {user.role}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-zinc-100">
                          €{user.balance.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-zinc-100">
                          €{user.liability_limit.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-zinc-400 hover:text-zinc-100"
                              onClick={() => handleEditUser(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {user.username !== protectedUsername && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-400 hover:text-red-300"
                                onClick={() => handleDeleteUser(user.id)}
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                            {user.username === protectedUsername && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded">
                                Protected
                              </span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit User Dialog */}
        <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-zinc-100">Edit User: {selectedUser?.username}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <Wallet className="w-4 h-4" />
                  Balance
                </Label>
                <Input
                  type="number"
                  value={editForm.balance}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      balance: safeNumber(e.target.value, editForm.balance),
                    })
                  }
                  className="bg-black border-zinc-800 text-zinc-100"
                  data-testid="edit-balance-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Liability Limit
                </Label>
                <Input
                  type="number"
                  value={editForm.liability_limit}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      liability_limit: safeNumber(e.target.value, editForm.liability_limit),
                    })
                  }
                  className="bg-black border-zinc-800 text-zinc-100"
                  data-testid="edit-liability-input"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded border border-zinc-800">
                <div>
                  <Label className="text-zinc-300">Show Exposure Panel</Label>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Display balance, exposure, limit on trader screen
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditForm({ ...editForm, show_exposure: !editForm.show_exposure })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    editForm.show_exposure ? 'bg-blue-600' : 'bg-zinc-700'
                  }`}
                  data-testid="show-exposure-toggle"
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                    editForm.show_exposure ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              <div className="flex items-center justify-between p-3 bg-red-950/20 rounded border border-red-900/30">
                <div>
                  <Label className="text-zinc-300">Disable Limits</Label>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Bypass balance and liability checks for this trader
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditForm({ ...editForm, disable_limits: !editForm.disable_limits })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    editForm.disable_limits ? 'bg-red-600' : 'bg-zinc-700'
                  }`}
                  data-testid="disable-limits-toggle"
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                    editForm.disable_limits ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Reset Password (optional)</Label>
                <Input
                  type="password"
                  value={editPassword}
                  onChange={(e) => setEditPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="bg-black border-zinc-800 text-zinc-100"
                  data-testid="edit-password-input"
                />
                <p className="text-xs text-zinc-500">
                  Leave blank to keep the current password.
                </p>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" className="border-zinc-700 text-zinc-400">Cancel</Button>
              </DialogClose>
              <Button 
                onClick={handleUpdateUser}
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="update-user-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
